import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import useSnackbar from '../../../hooks/useSnackbar';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import { boldXxl, mediumLg, regularSm, spHeaderTitle } from '../../../constants/location';
import { gray80, gray100, white, gray40 } from '../../../constants/colors';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { getWebhookEndpointsByPatternId } from '../../../api/firebase/firestore/webhookEndpoints/read';
import {
  deleteWebhookEndpoint,
  getRecentWebhookDelivery,
  rotateWebhookSecret,
  saveWebhookEndpoint,
  sendWebhookTest,
} from '../../../api/firebase/function/webhook/webhookConfigFunction';
import { PatternType } from '../../../types/pattern';
import {
  RecentWebhookDeliveryType,
  WebhookEndpointFormValues,
  WebhookEndpointType,
} from '../../../types/webhook';
import { WebhookEventType, webhookEventType, webhookEventTypeLabel } from '../../../constants/enum';
import { hasAdminPermission, isOperatorStaff } from '../../../utils/permission';
import { getUserErrorMessage } from '../../../modules/error';
import {
  WEBHOOK_BACK_TO_PATTERN,
  WEBHOOK_DELETE_SUCCESS,
  WEBHOOK_FEATURE_DISABLED,
  WEBHOOK_LOAD_ERROR,
  WEBHOOK_PAGE_TITLE,
  WEBHOOK_SAVE_ERROR,
  WEBHOOK_SAVE_SUCCESS,
  WEBHOOK_TEST_FAILED,
  WEBHOOK_TEST_SUCCESS,
} from '../../../constants/labels';
import WebhookEndpointForm from './components/WebhookEndpointForm';

const styles = {
  headerTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  mainContainer: {
    flexDirection: 'column',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    backgroundColor: white,
    margin: '0px 32px 16px 32px',
    padding: '24px',
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      margin: '0',
      width: '100%',
      border: 'none',
      borderRadius: 0,
    },
  },
  sectionTitle: {
    ...mediumLg,
    color: gray100,
    marginBottom: '16px',
  },
};

/**
 * パターン設定画面の Webhook 設定タブ（spec §7.1 / §12-3）
 * - 企業の webhook_enabled = true の場合のみ操作可能
 * - 確認済み（result.reviewed）／ファイル出力済み（result.exported）の2セクション構成
 */
const PatternWebhookSetting: React.FC = () => {
  const { patternId } = useParams<{ patternId: string }>();
  const { user, isLoading } = useContext(AuthContext);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();

  const [pattern, setPattern] = useState<PatternType | null>(null);
  const [companyWebhookEnabled, setCompanyWebhookEnabled] = useState<boolean>(false);
  const [reviewedEndpoint, setReviewedEndpoint] = useState<WebhookEndpointType | null>(null);
  const [exportedEndpoint, setExportedEndpoint] = useState<WebhookEndpointType | null>(null);
  const [reviewedSecret, setReviewedSecret] = useState<string | null>(null);
  const [exportedSecret, setExportedSecret] = useState<string | null>(null);
  const [reviewedDelivery, setReviewedDelivery] = useState<RecentWebhookDeliveryType | null>(null);
  const [exportedDelivery, setExportedDelivery] = useState<RecentWebhookDeliveryType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);

  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();

  // 初回ロード
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      return;
    }
    if (!hasAdminPermission(user.role) && !isOperatorStaff(user.role)) {
      // 企業管理者または運営スタッフ以外は閲覧不可
      navigate(`/ai-ocr/pattern/${patternId}`);
      return;
    }
    if (!patternId) return;

    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        const patternData = await getPattern(patternId);
        if (!patternData) {
          setErrorText(WEBHOOK_LOAD_ERROR);
          setSnackbarOpen(true);
          navigate('/pattern');
          return;
        }
        setPattern(patternData);

        // 企業の webhook_enabled を確認
        const companyId = patternData.company_id ?? user.company_id;
        if (companyId) {
          const companyData = await getCompany(companyId);
          setCompanyWebhookEnabled(companyData?.webhook_enabled === true);
        }

        const existing = companyId
          ? await getWebhookEndpointsByPatternId(patternId, companyId)
          : [];
        const reviewed =
          existing.find(e => e.event_type === webhookEventType.resultReviewed) ?? null;
        const exported =
          existing.find(e => e.event_type === webhookEventType.resultExported) ?? null;
        setReviewedEndpoint(reviewed);
        setExportedEndpoint(exported);
        if (reviewed) {
          setReviewedDelivery(
            await getRecentWebhookDelivery({ endpoint_id: reviewed.endpoint_id }),
          );
        }
        if (exported) {
          setExportedDelivery(
            await getRecentWebhookDelivery({ endpoint_id: exported.endpoint_id }),
          );
        }
      } catch (error) {
        Sentry.captureException(error);
        setErrorText(WEBHOOK_LOAD_ERROR);
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [isLoading, user, patternId, navigate, setErrorText, setSnackbarOpen]);

  const applyEndpointResult = useCallback(
    (
      eventType: WebhookEventType,
      endpoint: WebhookEndpointType | null,
      secret: string | null,
    ): void => {
      if (eventType === webhookEventType.resultReviewed) {
        setReviewedEndpoint(endpoint);
        setReviewedSecret(secret);
      } else {
        setExportedEndpoint(endpoint);
        setExportedSecret(secret);
      }
    },
    [],
  );

  const handleSave = useCallback(
    async (eventType: WebhookEventType, values: WebhookEndpointFormValues): Promise<void> => {
      if (!patternId) return;
      try {
        setSaving(true);
        const response = await saveWebhookEndpoint({
          pattern_id: patternId,
          enabled: values.enabled,
          url: values.url,
          event_type: eventType,
        });
        applyEndpointResult(eventType, response.endpoint, response.plaintext_secret ?? null);
        setInfoText(WEBHOOK_SAVE_SUCCESS);
        setSnackbarOpen(true);
      } catch (error) {
        Sentry.captureException(error);
        const message = getUserErrorMessage(error, WEBHOOK_SAVE_ERROR);
        setErrorText(message);
        setSnackbarOpen(true);
      } finally {
        setSaving(false);
      }
    },
    [patternId, applyEndpointResult, setErrorText, setInfoText, setSnackbarOpen],
  );

  const handleRotate = useCallback(
    async (eventType: WebhookEventType, endpointId: string): Promise<void> => {
      try {
        setSaving(true);
        const response = await rotateWebhookSecret({ endpoint_id: endpointId });
        applyEndpointResult(eventType, response.endpoint, response.plaintext_secret);
        setInfoText(WEBHOOK_SAVE_SUCCESS);
        setSnackbarOpen(true);
      } catch (error) {
        Sentry.captureException(error);
        const message = getUserErrorMessage(error, WEBHOOK_SAVE_ERROR);
        setErrorText(message);
        setSnackbarOpen(true);
      } finally {
        setSaving(false);
      }
    },
    [applyEndpointResult, setErrorText, setInfoText, setSnackbarOpen],
  );

  const handleDelete = useCallback(
    async (eventType: WebhookEventType, endpointId: string): Promise<void> => {
      try {
        setSaving(true);
        await deleteWebhookEndpoint({ endpoint_id: endpointId });
        if (eventType === webhookEventType.resultReviewed) {
          setReviewedEndpoint(null);
          setReviewedSecret(null);
          setReviewedDelivery(null);
        } else {
          setExportedEndpoint(null);
          setExportedSecret(null);
          setExportedDelivery(null);
        }
        setInfoText(WEBHOOK_DELETE_SUCCESS);
        setSnackbarOpen(true);
      } catch (error) {
        Sentry.captureException(error);
        const message = getUserErrorMessage(error, WEBHOOK_SAVE_ERROR);
        setErrorText(message);
        setSnackbarOpen(true);
      } finally {
        setSaving(false);
      }
    },
    [setErrorText, setInfoText, setSnackbarOpen],
  );

  const handleTestSend = useCallback(
    async (eventType: WebhookEventType, endpointId: string): Promise<void> => {
      setTesting(true);
      try {
        const response = await sendWebhookTest({
          endpoint_id: endpointId,
          event_type: eventType,
        });
        const recent = await getRecentWebhookDelivery({ endpoint_id: endpointId });
        if (eventType === webhookEventType.resultReviewed) {
          setReviewedDelivery(recent);
        } else {
          setExportedDelivery(recent);
        }
        if (response.error_reason) {
          setErrorText(`${WEBHOOK_TEST_FAILED}: ${response.error_reason}`);
        } else {
          setInfoText(`${WEBHOOK_TEST_SUCCESS} (HTTP ${response.http_status ?? '-'})`);
        }
        setSnackbarOpen(true);
      } catch (error) {
        Sentry.captureException(error);
        const message = getUserErrorMessage(error, WEBHOOK_SAVE_ERROR);
        setErrorText(message);
        setSnackbarOpen(true);
      } finally {
        setTesting(false);
      }
    },
    [setErrorText, setInfoText, setSnackbarOpen],
  );

  if (isLoading || loading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle={WEBHOOK_PAGE_TITLE} />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle
        customTitle={
          pattern ? `${pattern.pattern_name} - ${WEBHOOK_PAGE_TITLE}` : WEBHOOK_PAGE_TITLE
        }
      />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <CustomLoading loading={saving} />

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { gap: '16px' },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box sx={styles.headerTitleWrapper}>
            <IconButton
              aria-label={WEBHOOK_BACK_TO_PATTERN}
              onClick={(): void => navigate(`/ai-ocr/pattern/${patternId}`)}
            >
              <ArrowBack sx={{ fontSize: '24px', color: gray80 }} />
            </IconButton>
            <Typography sx={styles.headerTitle}>
              {pattern?.pattern_name} - {WEBHOOK_PAGE_TITLE}
            </Typography>
          </Box>
        </Box>

        {!companyWebhookEnabled ? (
          <Box sx={styles.mainContainer}>
            <Typography sx={{ ...regularSm, color: gray80 }}>{WEBHOOK_FEATURE_DISABLED}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={styles.mainContainer}>
              <Typography sx={styles.sectionTitle}>
                {webhookEventTypeLabel[webhookEventType.resultReviewed]}
              </Typography>
              <WebhookEndpointForm
                initialEndpoint={reviewedEndpoint}
                plaintextSecret={reviewedSecret}
                recentDelivery={reviewedDelivery}
                saving={saving}
                testing={testing}
                onSave={(values): Promise<void> =>
                  handleSave(webhookEventType.resultReviewed, values)
                }
                onRotate={(): Promise<void> =>
                  reviewedEndpoint
                    ? handleRotate(webhookEventType.resultReviewed, reviewedEndpoint.endpoint_id)
                    : Promise.resolve()
                }
                onDelete={(): Promise<void> =>
                  reviewedEndpoint
                    ? handleDelete(webhookEventType.resultReviewed, reviewedEndpoint.endpoint_id)
                    : Promise.resolve()
                }
                onTestSend={
                  reviewedEndpoint
                    ? (): Promise<void> =>
                        handleTestSend(
                          webhookEventType.resultReviewed,
                          reviewedEndpoint.endpoint_id,
                        )
                    : undefined
                }
                onClearPlaintextSecret={(): void => setReviewedSecret(null)}
              />
            </Box>
            <Box sx={styles.mainContainer}>
              <Typography sx={styles.sectionTitle}>
                {webhookEventTypeLabel[webhookEventType.resultExported]}
              </Typography>
              <WebhookEndpointForm
                initialEndpoint={exportedEndpoint}
                plaintextSecret={exportedSecret}
                recentDelivery={exportedDelivery}
                saving={saving}
                testing={testing}
                onSave={(values): Promise<void> =>
                  handleSave(webhookEventType.resultExported, values)
                }
                onRotate={(): Promise<void> =>
                  exportedEndpoint
                    ? handleRotate(webhookEventType.resultExported, exportedEndpoint.endpoint_id)
                    : Promise.resolve()
                }
                onDelete={(): Promise<void> =>
                  exportedEndpoint
                    ? handleDelete(webhookEventType.resultExported, exportedEndpoint.endpoint_id)
                    : Promise.resolve()
                }
                onTestSend={
                  exportedEndpoint
                    ? (): Promise<void> =>
                        handleTestSend(
                          webhookEventType.resultExported,
                          exportedEndpoint.endpoint_id,
                        )
                    : undefined
                }
                onClearPlaintextSecret={(): void => setExportedSecret(null)}
              />
            </Box>
          </>
        )}
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </Box>
  );
};

export default PatternWebhookSetting;
